import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../../i18n/format";
import type { Translate } from "../../i18n/translate";

/**
 * How playback analytics numbers are said. One place, so a tile, a table cell,
 * a tooltip and a CSV header never disagree about rounding or units.
 */

const EMPTY = "–";

type Maybe = number | null | undefined;

export interface PlaybackFormat {
  count: (v: Maybe) => string;
  /** Hours: one decimal under 100, whole hours above. */
  hours: (v: Maybe) => string;
  minutes: (v: Maybe) => string;
  /** A rate (0–1) as a percent; sub-1 % values keep a decimal. */
  percent: (v: Maybe) => string;
  ms: (v: Maybe) => string;
  kbps: (v: Maybe) => string;
  perHour: (v: Maybe) => string;
  /** Ad time: seconds under a minute, minutes under an hour, then hours. */
  adTime: (seconds: Maybe) => string;
  change: (c: number | null) => string;
  /** `2026-10-05` → `5 Oct`; `2026-10-05T14:00` → `14:00`. */
  bucket: (bucket: string, granularity: "day" | "hour") => string;
  day: (day: string) => string;
}

/**
 * Relative change vs the previous period, or null when there is no fair
 * comparison (nothing before, or nothing now and nothing before).
 */
export function change(now: Maybe, before: Maybe): number | null {
  if (
    now === null ||
    now === undefined ||
    before === null ||
    before === undefined
  ) {
    return null;
  }
  if (before === 0) return now === 0 ? 0 : null;
  return (now - before) / Math.abs(before);
}

export function playbackFormat(f: Formatters, t: Translate): PlaybackFormat {
  const fixed = (v: number, digits: number) =>
    f.number(v, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  const n0 = (v: number) => f.number(v, { maximumFractionDigits: 0 });
  const n1 = (v: number) => f.number(v, { maximumFractionDigits: 1 });
  const unit = (key: string, value: string) =>
    String(t(`analytics:playbackFormat.${key}`, { value }));
  const utcDate = (iso: string, options: Intl.DateTimeFormatOptions) => {
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return iso;
    return f.date(d, { ...options, timeZone: "UTC" });
  };
  return {
    count: (v) => (v === null || v === undefined ? EMPTY : n0(v)),
    hours: (v) =>
      v === null || v === undefined
        ? EMPTY
        : unit("hours", v < 100 ? n1(v) : n0(v)),
    minutes: (v) =>
      v === null || v === undefined ? EMPTY : unit("minutes", n1(v)),
    percent: (v) => {
      if (v === null || v === undefined) return EMPTY;
      const p = v * 100;
      return unit("percent", fixed(p, p < 1 && p > 0 ? 2 : p < 10 ? 1 : 0));
    },
    ms: (v) =>
      v === null || v === undefined
        ? EMPTY
        : v < 1000
          ? unit("milliseconds", n0(Math.round(v)))
          : unit("seconds", fixed(v / 1000, 1)),
    kbps: (v) =>
      v === null || v === undefined
        ? EMPTY
        : v >= 1000
          ? unit("mbps", fixed(v / 1000, 1))
          : unit("kbps", n0(Math.round(v))),
    perHour: (v) =>
      v === null || v === undefined ? EMPTY : unit("perHour", n1(v)),
    adTime: (seconds) =>
      seconds === null || seconds === undefined
        ? EMPTY
        : seconds < 60
          ? unit("seconds", n0(Math.round(seconds)))
          : seconds < 3600
            ? unit("minutes", n1(seconds / 60))
            : unit("hours", n1(seconds / 3600)),
    change: (c) => {
      if (c === null) return String(t("analytics:playbackFormat.noPrevious"));
      const p = c * 100;
      if (Math.abs(p) < 0.5)
        return String(t("analytics:playbackFormat.unchanged"));
      const size =
        Math.abs(p) < 10 ? fixed(Math.abs(p), 1) : n0(Math.round(Math.abs(p)));
      return unit("percent", `${p > 0 ? "+" : "−"}${size}`);
    },
    bucket: (bucket, granularity) =>
      granularity === "hour"
        ? bucket.slice(11, 16)
        : utcDate(bucket.slice(0, 10), { day: "numeric", month: "short" }),
    day: (day) =>
      utcDate(day, { day: "numeric", month: "short", year: "numeric" }),
  };
}

/** The playback formatters, following the CMS language. */
export function usePlaybackFormat(): PlaybackFormat {
  const f = useFormat();
  const { t } = useTranslation("analytics");
  return useMemo(() => playbackFormat(f, t), [f, t]);
}
